// hooks/useDashboardData.js - Hook for fetching real dashboard data
import { useState, useEffect, useCallback } from 'react';
import { getDashboardData } from '../services/api/dashboardDataApi';

export const useDashboardData = (selectedTable = 'access_entries', timeWindowHours = 12, trendMode = 'per_range', originWindowHours = 3) => {
    const [dashboardData, setDashboardData] = useState({
        origins: [],
        destinations: [],
        uniqueDestinations: 0,
        loading: true,
        error: null,
        lastUpdated: null,
        cached: false,
        cacheAge: 0
    });

    const [autoRefresh, setAutoRefresh] = useState(true);

    // Fetch dashboard data from API
    const fetchDashboardData = useCallback(async (forceRefresh = false) => {
        try {
            console.log(`🔄 Fetching dashboard data for table: ${selectedTable}${forceRefresh ? ' (force refresh)' : ''}`);

            const result = await getDashboardData(selectedTable, {
                forceRefresh,
                timeWindowHours,
                trendMode,
                originWindowHours
            });

            if (result.success) {
                setDashboardData({
                    origins: result.origins || [],
                    destinations: result.destinations || [],
                    uniqueDestinations: result.unique_destinations || 0,
                    loading: false,
                    error: null,
                    lastUpdated: new Date(result.timestamp),
                    cached: result.cached || false,
                    cacheAge: result.cache_age_minutes || 0,
                    dataPeriod: result.data_period,
                    originPeriod: result.origin_period,
                    trendMode: result.trend_mode
                });

                console.log(`✅ Dashboard data loaded successfully:`, {
                    origins: result.origins.length,
                    destinations: result.destinations.length,
                    uniqueDestinations: result.unique_destinations,
                    cached: result.cached,
                    cacheAge: result.cache_age_minutes
                });
            } else {
                throw new Error(result.error || 'Failed to fetch dashboard data');
            }

        } catch (error) {
            console.error('❌ Error fetching dashboard data:', error);
            setDashboardData(prev => ({
                ...prev,
                loading: false,
                error: error.message
            }));
        }
    }, [selectedTable, timeWindowHours, trendMode, originWindowHours]);

    // Force refresh data
    const refreshData = useCallback(() => {
        setDashboardData(prev => ({ ...prev, loading: true }));
        fetchDashboardData(true);
    }, [fetchDashboardData]);

    // Fetch once per selected table/window change. Keeping this as a single effect
    // prevents overlapping initial/table/window requests from locking the UI in loading.
    useEffect(() => {
        setDashboardData(prev => ({ ...prev, loading: true }));
        fetchDashboardData(false);
    }, [fetchDashboardData]);

    useEffect(() => {
        if (!autoRefresh) return undefined;
        const interval = setInterval(() => {
            console.log('⏰ Auto-refreshing dashboard data...');
            fetchDashboardData(false);
        }, 900000);
        return () => clearInterval(interval);
    }, [autoRefresh, fetchDashboardData]);

    return {
        // Data
        origins: dashboardData.origins,
        destinations: dashboardData.destinations,
        uniqueDestinations: dashboardData.uniqueDestinations,

        // Status
        loading: dashboardData.loading,
        error: dashboardData.error,
        lastUpdated: dashboardData.lastUpdated,
        cached: dashboardData.cached,
        cacheAge: dashboardData.cacheAge,
        dataPeriod: dashboardData.dataPeriod,
        originPeriod: dashboardData.originPeriod,
        trendMode: dashboardData.trendMode,

        // Controls
        autoRefresh,
        setAutoRefresh,
        refreshData,

        // Utils
        isStale: dashboardData.cacheAge > 60, // Consider stale if over 1 hour
        isEmpty: dashboardData.origins.length === 0 && dashboardData.destinations.length === 0,
        hasData: dashboardData.origins.length > 0 || dashboardData.destinations.length > 0
    };
};
